import {
  Skeleton,
  Table,
  Tbody,
  Thead,
  Tr,
  Td,
  Th,
  Text,
  Icon,
  Flex,
  Box,
  Heading,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import { FundingVerificationPipelineRunGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { useGetActionableFundingVerificationPipelineRunsQuery } from '../requests/requests.gql.generated';
import { VerificationStatusCellContent } from './VerificationStatusCellContent';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { AiFillCheckCircle } from 'react-icons/ai';

// @dani verification - add click to go into a pipeline page with those 3 captured in context
// new page with have everything disabled aside from calculating panel and approve table panel

const verificationTableHeaderLabels = [
  'Reporting Date',
  'Facility Name',
  'Report Type',
  'Progress',
];

const VerificationTrackerTableRow = ({
  actionablePipelineRun,
}: {
  actionablePipelineRun: FundingVerificationPipelineRunGQLType;
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { navigator } = useVerificationOptions();

  const {
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion,
    // goalArtifactIds,
    status: pipelineStatus,
  } = actionablePipelineRun;

  const pipelineSelectCallback = useCallback(() => {
    navigator(navigate).setPipeline({
      artifactDocumentId,
      creditFacilityId,
      reportingDate,
      fileVersion,
    });
  }, [
    navigate,
    navigator,
    artifactDocumentId,
    creditFacilityId,
    reportingDate,
    fileVersion,
  ]);

  const {
    data: creditFacilityData,
    loading: creditFacilityLoading,
  } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  if (!creditFacilityData || creditFacilityLoading) {
    return (
      <Tr
        key={`loading-pipeline-run-${artifactDocumentId}-${reportingDate}-${fileVersion}`}
      >
        {verificationTableHeaderLabels.map((header) => (
          <Td key={`skeleton-${header}`}>
            <Skeleton
              height="25px"
              w="60%"
              data-testid="verification-tracker-table-row-loading"
            />
          </Td>
        ))}
      </Tr>
    );
  }

  if (pipelineStatus === 'complete') {
    return <></>;
  }

  // @dani verification - replace with entry artifact type of the graph
  const entryArtifactType = 'Servicer Report';

  return (
    <Tr
      _hover={{ background: 'white' }}
      cursor="pointer"
      onClick={pipelineSelectCallback}
    >
      <Td textAlign="left">
        {commonDate.fullDateStrToHumanDateString(reportingDate)}
      </Td>
      <Td textAlign="left">{creditFacilityData.creditFacility.name}</Td>
      <Td textAlign="left">{entryArtifactType}</Td>
      <Td textAlign="left">
        <VerificationStatusCellContent pipelineStatus={pipelineStatus} />
      </Td>
    </Tr>
  );
};

export const VerificationTrackerTable = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const toast = useToast();
  const {
    data: actionablePipelineRunsData,
    startPolling: startPollingActionablePipelineRuns,
    stopPolling: stopPollingActionablePipelineRuns,
  } = useGetActionableFundingVerificationPipelineRunsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      organizationId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    startPollingActionablePipelineRuns(5000);
    return () => stopPollingActionablePipelineRuns();
  }, [startPollingActionablePipelineRuns, stopPollingActionablePipelineRuns]);

  if (!actionablePipelineRunsData) {
    return (
      <Skeleton
        height="600px"
        data-testid="incomplete-verifications-tracker-loading"
      />
    );
  }

  const {
    actionableFundingVerificationPipelineRuns: actionablePipelineRuns,
  } = actionablePipelineRunsData;

  if (!actionablePipelineRuns.length) {
    return (
      <Flex>
        <Icon
          color="green.200"
          as={AiFillCheckCircle}
          marginRight="12px"
          fontSize="28px"
        />
        <Text>There are currently no ongoing verifications processes.</Text>
      </Flex>
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="8px"
      padding="24px"
      background={colors.millet[50]}
    >
      <Heading marginBottom="16px" as="h3" size="md">
        Verification Tracker
      </Heading>
      <Table variant="simple">
        <Thead borderBottom="1px solid lightgrey" padding="8px 0">
          <Tr>
            {verificationTableHeaderLabels.map((label) => (
              <Th textAlign="left" key={`header-${label}`}>
                {label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {actionablePipelineRuns.map((pipelineRun) => (
            <VerificationTrackerTableRow
              actionablePipelineRun={pipelineRun}
              key={`pipeline-run-${pipelineRun.artifactDocumentId}-${pipelineRun.reportingDate}-${pipelineRun.fileVersion}`}
            />
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
